/* global Klarna */

// Api docs: https://developers.klarna.com/api/#metadata

import axios from 'axios'
import urlConfig from 'services/urlConfig.js'

import logScope from 'log-scope'
const log = logScope('Klarna')

const klarnaApi = {
  url: urlConfig.rideqEmail,
  instance: axios.create({
    baseURL: urlConfig.rideqEmail,
    headers: {}
  }),
  post: (route, body) => klarnaApi.instance.post(route, body),
  get: (route, body) => klarnaApi.instance.get(route, body)
}

const delay = async (duration) => new Promise((resolve) => {
  setTimeout(resolve, duration)
})

const klarna = {
  isInitialized: false,
  clientToken: 'eyJhbGciOiJSUzI1NiJ9.ewogICJzZXNzaW9uX2lkIiA6ICIyZWFlZDNkMC0xMDQ4LTdhNzgtODUzNS0zMmE5MTdiODgyMDAiLAogICJiYXNlX3VybCIgOiAiaHR0cHM6Ly9rbGFybmEtcGF5bWVudHMtZXUucGxheWdyb3VuZC5rbGFybmEuY29tIiwKICAiZGVzaWduIiA6ICJrbGFybmEiLAogICJsYW5ndWFnZSIgOiAic3YiLAogICJwdXJjaGFzZV9jb3VudHJ5IiA6ICJTRSIsCiAgImFuYWx5dGljc19wcm9wZXJ0eV9pZCIgOiAiVUEtMzYwNTMxMzctMTEiLAogICJ0cmFjZV9mbG93IiA6IGZhbHNlLAogICJlbnZpcm9ubWVudCIgOiAicGxheWdyb3VuZCIsCiAgIm1lcmNoYW50X25hbWUiIDogIllvdXIgYnVzaW5lc3MgbmFtZSIsCiAgInNlc3Npb25fdHlwZSIgOiAiUEFZTUVOVFMiLAogICJjbGllbnRfZXZlbnRfYmFzZV91cmwiIDogImh0dHBzOi8vZXZ0LnBsYXlncm91bmQua2xhcm5hLmNvbSIsCiAgImV4cGVyaW1lbnRzIiA6IFsgXQp9.juVKr0AXbG9WpU2S7kDFQTQQ3RQaQN2K_eLHoEmO0dRc7t1B3xsskmG0-DamvsdR1a3C07PD4gtQuV3rpZgHXamjYyF-1Vvfq9U_0rN40R10EKiz8Bjo_9QYVj-qKKMXvoKll5cfBhbpC9YkB8GweVCZIYpbLhb6Lq7FbXBpbiNZvqS6dSDr-maEqo-Iwd7rVxSxsTocNev1lJExZowBoP-dUY6nivOaT4dbkE0wuZreoU0LYgusJ6wHEFzx4MMtm4XrEmNbL5Zt-mhkztljG1qOxu2qZRDJKXNCa3QAwB8P00AOIyKXwSStIrvb5d95IQbZ_UL9P5jjhnjNHsFXUw',
  sessionId: '',
  authorizationToken: '9aa240c6-d5c5-7c25-a455-7b323af61148',
  finalAuthorizationToken: '',
  paymentSettings: {
    payment_method_category: 'pay_now'
  },
  purchaseData: {
    auto_capture: true,
    purchase_country: 'SE',
    purchase_currency: 'SEK',
    locale: 'en-gb',
    order_amount: 0,
    order_tax_amount: 0,
    order_lines: [{
      type: 'physical',
      reference: '1',
      name: 'HeadQ',
      quantity: 0,
      unit_price: 0, // in öre
      tax_rate: 2500,
      total_amount: 0,
      total_discount_amount: 0, // inc. tax. non-negative in minor units (öre)
      total_tax_amount: 0,
      product_url: 'https://www.rideq.se/order'
    }]
  }
}

window.klarnaAsyncCallback = function () {
  log.debug('Klarna async callback')
  log.todo('Dev mode, resume klarna.init before deploy')
  // klarna.init()
}

klarna.init = async () => {
  // DOCS: https://developers.klarna.com/documentation/klarna-payments/integration-guide/create-session/
  if (klarna.isInitialized === true) {
    log.debug('Already initialized')
    return
  }

  if (!window.Klarna || !Klarna || !Klarna.Payments) {
    log.warning('Klarna has not yet been loaded')
    return
  }

  const locale = window.k$.lang === 'sv' ? 'sv-se' : 'en-gb'
  klarna.purchaseData.locale = locale

  try {
    await klarna.createSession()

    Klarna.Payments.init({
      client_token: klarna.clientToken
    })

    await delay(1)

    klarna.isInitialized = true
  } catch (err) {
    log.error('Could not initialize', err)
  }
}

klarna.createSession = async () => {
  // DOCS: https://developers.klarna.com/documentation/klarna-payments/integration-guide/create-session

  log.todo('Send shopping cart details, country, corrency and locale to session')

  const res = await klarnaApi.post('createKlarnaSession', { data: klarna.purchaseData })

  klarna.clientToken = res.data.data.client_token
  klarna.sessionId = res.data.data.session_id
}

klarna.showPaymentOptions = async () => {
  // DOCS:
  // https://developers.klarna.com/documentation/klarna-payments/integration-guide/present-klarna-widget/
  // https://credit.klarnacdn.net/lib/v1/index.html

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error('Klarna widget load timeout'))
    }, 10000)

    Klarna.Payments.load({
      container: '#klarna_container',
      ...klarna.paymentSettings
    }, (res) => {
      if (res.show_form === true) {
        resolve()
        return
      }

      reject(new Error('Klarna widget now showing'))
    })
  })
}

klarna.pay = async ({ couponCode, partnerCode, abTestCode }) => {
  await klarna.authorize()

  const order = await klarna.placeOrder({ couponCode, partnerCode, abTestCode })

  log.success('Order completed!')
  return order
}

klarna.authorize = async () => {
  // DOCS: https://developers.klarna.com/documentation/klarna-payments/integration-guide/authorize/

  log.debug('Authorizing purchase')

  log.todo('Avoid sending another auth request while pending')
  log.todo('UI display pending')
  log.todo('Prevent user to changing order or info while pending')

  return new Promise((resolve, reject) => {
    Klarna.Payments.authorize(klarna.paymentSettings, klarna.purchaseData, (res) => {
      if (!res || !res.approved) {
        log.error('Auth not approved', {
          res: res,
          paymentSettings: klarna.paymentSettings,
          purchaseData: klarna.purchaseData
        })
        reject(new Error('Auth not approved'))
        return
      }

      if (res.show_form === false) {
        log.error('Payments not available', res)
        reject(new Error('Payments not available'))
        return
      }

      if (res.approved === false) {
        log.error('Payment denied', res)
        reject(new Error('Payment denied'))
        return
      }

      klarna.authorizationToken = res.authorization_token
      resolve()
    })
  })
}

klarna.placeOrder = async ({ couponCode, partnerCode, abTestCode }) => {
  const res = await klarnaApi.post('placeKlarnaOrder', {
    authorization_token: klarna.authorizationToken,
    data: {
      ...klarna.purchaseData,
      merchant_urls: {
        confirmation: 'https://rideq.se/thanks'
      }
    },
    couponCode: couponCode,
    partnerCode: partnerCode,
    abTestCode: abTestCode
  })
  const data = res.data.data
  const order = res.data.order

  log.success('Order placed', data, order, couponCode)

  return order
}

// non-api methods
klarna.updateOrder = (orderLines) => {
  klarna.purchaseData.order_amount = 0
  klarna.purchaseData.order_tax_amount = 0

  orderLines.forEach(order => {
    klarna.purchaseData.order_amount += order.total_amount
    klarna.purchaseData.order_tax_amount += order.total_tax_amount
  })

  klarna.purchaseData.order_lines = orderLines
  log.debug('Updated order', orderLines)
}

// Only for Pay Now
klarna.finalize = () => {
  // DOCS: https://developers.klarna.com/documentation/klarna-payments/integration-guide/authorize/
  log.debug('Finalizing purchase')

  Klarna.Payments.finalize(klarna.paymentSettings, {}, (res) => {
    if (!res || !res.approved) {
      log.error('Finalization not approved', res)
      return
    }

    log.success('Post finalization', res)
    klarna.finalAuthorizationToken = res.authorization_token // Token valid for 60min
  })
}

export default klarna
